// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import { ButtonPresentationProps } from "@werner/MobiusOverrides/Button/Button";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import GhostButton from "@werner/XDL/Icons/GhostButton";
import { xBaseTheme } from "@werner/XDL/types";
import color from "@werner/XDL/Utilities/color";
import { getSystemColor } from "@werner/XDL/Utilities/color";
import { css } from "styled-components";

export interface ButtonPreStyles {
    defaultProps?: ButtonPresentationProps;
    primary: ButtonPresentationProps;
    secondary: ButtonPresentationProps;
    tertiary: ButtonPresentationProps;
}

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

export const buttonColors: { [key: string]: { main: string; hover: string; text: string; focus: string } } = {
    primary: {
        main: "primary",
        hover: "primary",
        text: "common.white",
        focus: "primary",
    },
    secondary: {
        main: "primary",
        hover: "common.ui3",
        text: "primary",
        focus: "primary",
    },
    tertiary: {
        main: "primary",
        hover: "common.ui3",
        text: "primary",
        focus: "primary",
    },
};

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.primary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
            color: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        icon: {
            src: CaretRight,
            position: "right",
            color: "secondary",
        },
        hoverAnimation: "bounceRight",
        typographyProps: {
            mergeCss: true,
            weight: 500,
        },
        css: css`
            border-radius: 3px;
            &:focus {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
                box-shadow: 0 0 8px 0 ${getSystemColor(buttonColors.primary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.main, 0.5)};
                border-color: transparent;
            }
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        mergeCss: true,
        buttonType: "outline",
        color: buttonColors.secondary.main,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.secondary.hover)}`,
            color: `${getSystemColor(buttonColors.secondary.hover)}`,
        },
        typographyProps: {
            mergeCss: true,
            weight: 500,
        },
        css: css`
            border: 2px solid;
            border-radius: 3px;
            justify-content: center;
            &:focus {
                border-color: ${getSystemColor(buttonColors.secondary.focus)};
                box-shadow: 0 0 8px 0 ${getSystemColor(buttonColors.secondary.focus)};
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.secondary.main, 0.5)};
                color: ${getSystemColor(buttonColors.secondary.main, 0.5)};
            }
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.tertiary.hover)}`,
            color: `${getSystemColor(buttonColors.tertiary.hover)}`,
        },
        typographyProps: {
            mergeCss: true,
            weight: 500,
        },
        icon: {
            src: GhostButton,
            position: "right",
            color: "primary",
        },
        css: css`
            min-width: unset;
            border: none;
            &:focus {
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
                color: ${getSystemColor(buttonColors.tertiary.focus)};
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.tertiary.main, 0.5)};
                color: ${getSystemColor(buttonColors.tertiary.main, 0.5)};
                svg {
                    fill: ${getSystemColor(buttonColors.tertiary.main, 0.5)};
                    color: ${getSystemColor(buttonColors.tertiary.main, 0.5)};
                }
            }
            &:hover:not(:disabled) {
                svg {
                    fill: ${getSystemColor(buttonColors.tertiary.hover)};
                    color: ${getSystemColor(buttonColors.tertiary.hover)};
                }
            }
        `,
    },
} as ButtonPreStyles;
